import type { components, paths } from "@/api-schema";
import {
  PathsV1ApplicationSearchPostParametersQueryExtend,
  PathsV1ApplicationSearchPostParametersQueryOrderByDirection,
  PathsV1ApplicationSearchPostParametersQueryOrderByField,
  PathsV1AppRoleAttributeSearchPostParametersQueryExtend,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useApplicationDeleteMutation } from "@/common/api/application/use-application-delete.mutation";
import { useApplicationSearchQuery } from "@/common/api/application/use-application-search.query";
import { useAttributeSearchQuery } from "@/common/api/attribute/use-attribute-search.query";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures } from "@/components/data-table/types";
import i18next from "i18next";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "../_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "../_components/base-predefined-table.types";
import { ApplicationAppRolesSubComponent } from "./_components/application-app-role-sub-component";
import { applicationAppRoleTableDefaultColumns } from "./application-app-role-table-columns";

type Application = components["schemas"]["Application"] & RowWithIdentifier;
type AppRoleAttribute = components["schemas"]["AppRoleAttribute"];
type ApplicationSorting = NonNullable<paths["/v1/application/search"]["post"]["parameters"]["query"]["orderBy"]>;
type ApplicationQueryParams = paths["/v1/application/search"]["post"]["parameters"]["query"];
type ApplicationRequestBody = paths["/v1/application/search"]["post"]["requestBody"]["content"]["application/json"];
type ApplicationExtend = NonNullable<paths["/v1/application/search"]["post"]["parameters"]["query"]["extend"]>;

const DEFAULT_EXTEND: ApplicationExtend = [PathsV1ApplicationSearchPostParametersQueryExtend.AppRoles] as const;

const DEFAULT_COLUMNS = [
  applicationAppRoleTableDefaultColumns.columnName,
  applicationAppRoleTableDefaultColumns.columnSelected,
];

const APPLICATION_APP_ROLE_TABLE_CONFIG: PredefinedTableConfig<ApplicationSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1ApplicationSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1ApplicationSearchPostParametersQueryOrderByField.Name,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("common:SearchApplications"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: true,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: false,
    showView: false,
    showDelete: false,
  } as const,
  sortingMapping: {
    name: ["name"],
  } as const,
} as const;

const APPLICATION_APP_ROLE_TABLE_QUERY_HOOKS: DataFetchHooks<
  Application,
  ApplicationQueryParams,
  ApplicationRequestBody
> = {
  useSearchQuery: useApplicationSearchQuery,
  useDeleteMutation: useApplicationDeleteMutation,
} as const;

export interface ApplicationAppRoleTableProps extends BasePredefinedTableProps<
  Application,
  ApplicationSorting,
  ApplicationQueryParams,
  ApplicationRequestBody
> {
  selected?: components["schemas"]["AppRole"][];
  extend?: ApplicationExtend;
}

const BaseApplicationAppRoleTable = createBasePredefinedTable({
  config: APPLICATION_APP_ROLE_TABLE_CONFIG,
  queryHooks: APPLICATION_APP_ROLE_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function ApplicationAppRoleTable(props: ApplicationAppRoleTableProps) {
  const { t } = useTranslation("common");
  const {
    name = "application-app-role-table",
    createButtonText = t("ram:ManageRoles"),
    extend = DEFAULT_EXTEND,
    selected = [],
    ...restProps
  } = props;

  const attributesQuery = useAttributeSearchQuery({
    options: {
      parameters: {
        pageSize: -1,
        extend: [PathsV1AppRoleAttributeSearchPostParametersQueryExtend.Application] as const,
      },
    },
  });

  const attributesByUuid = useMemo(() => {
    const data = attributesQuery.data?.data;

    if (!data) {
      return new Map<string, AppRoleAttribute>();
    }

    return new Map<string, AppRoleAttribute>(data.map((attr) => [attr.uuid, attr]));
  }, [attributesQuery.data?.data]);

  const subComponent = useCallback(
    (subProps: { row: { original: Application } }) => {
      return (
        <ApplicationAppRolesSubComponent
          application={subProps.row.original}
          selected={selected}
          attributesByUuid={attributesByUuid}
        />
      );
    },
    [selected, attributesByUuid],
  );

  return (
    <BaseApplicationAppRoleTable
      searchPlaceholder={t("common:SearchApplications")}
      name={name}
      createButtonText={createButtonText}
      extend={extend}
      subComponent={subComponent}
      getRowCanExpand={(row) => {
        return row.appRoles && row.appRoles.length > 0;
      }}
      meta={{
        selected: selected.map((item) => item.uuid),
        attributesByUuid,
      }}
      {...restProps}
    />
  );
}
